import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  InputLabel,
  LinearProgress,
  MenuItem,
  Paper,
  Select,
  Snackbar,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { supabase } from '../config/supabase';
import { useAuditUserId } from '../auth/AuthContext';
import { legacyParticipationEventTypeLabels, participationEventReasonOptions, participationEventTypeOptions } from '../data/options';
import { lookupLabel } from '../data/lookups';
import type { LookupState, TimeFormat } from '../types';
import { formatTime } from '../utils/timeFormat';
import { SeasonTeamMultiSelect } from './SeasonTeamMultiSelect';
import { usePersistentScrollRestoration, usePersistentViewState } from '../hooks/usePersistentViewState';

type ParticipationEventsManagerProps = {
  lookups: LookupState;
  timeFormat: TimeFormat;
  initialPlayerFilterId?: string | null;
  readOnly?: boolean;
};

type ParticipationEventRow = {
  id: string;
  match_event_id: string;
  player_id: string;
  league_configuration_position_id: string | null;
  participation_event_type: string;
  allocation_count_indicator: boolean;
  manual_allocation_override_indicator: boolean;
  participation_event_reason: string | null;
  participation_event_notes: string | null;
  replaced_player_id: string | null;
  replacement_player_id: string | null;
  occurred_at: string;
  created_at: string;
  updated_at: string;
  created_by: string | null;
  updated_by: string | null;
};

type MatchOption = {
  id: string;
  team_id: string;
  league_configuration_id: string;
  match_date: string;
  match_time: string | null;
  opponent_name: string | null;
};

type PositionOption = {
  id: string;
  league_configuration_id: string;
  position_code: string;
  display_name: string;
};

type EventForm = {
  id: string | null;
  match_event_id: string;
  player_id: string;
  league_configuration_position_id: string;
  participation_event_type: string;
  participation_event_reason: string;
  allocation_count_indicator: boolean;
  participation_event_notes: string;
};

type Snack = {
  message: string;
  severity: 'success' | 'error' | 'info' | 'warning';
};
type ParticipationEventsViewState = {
  search: string;
  teamFilters: string[];
  playerFilter: string;
  eventTypeFilter: string;
};

const emptyEventForm: EventForm = {
  id: null,
  match_event_id: '',
  player_id: '',
  league_configuration_position_id: '',
  participation_event_type: 'administrative_adjustment',
  participation_event_reason: 'Other',
  allocation_count_indicator: false,
  participation_event_notes: '',
};

function formatDate(value: string | null | undefined): string {
  if (!value) return '';
  const [year, month, day] = value.split('-').map(Number);
  if (!year || !month || !day) return value;
  return new Date(year, month - 1, day).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
}

function eventTypeLabel(value: string): string {
  return participationEventTypeOptions.find((option) => option.value === value)?.label || legacyParticipationEventTypeLabels[value] || value;
}

function canonicalEventType(value: string): string {
  if (value === 'replacement') return 'late_substitute';
  if (value === 'administrative_correction') return 'administrative_adjustment';
  return value;
}

function defaultAllocationForEventType(value: string): boolean {
  return value === 'played' || value === 'cancelled_late' || value === 'late_substitute' || value === 'replacement';
}

function isAllowedParticipationReason(value: string | null | undefined): value is string {
  return participationEventReasonOptions.some((option) => option.value === value);
}

function editableEventTypeOptions(currentValue: string) {
  if (!currentValue || participationEventTypeOptions.some((option) => option.value === currentValue)) return participationEventTypeOptions;
  return [
    { value: currentValue, label: eventTypeLabel(currentValue) },
    ...participationEventTypeOptions,
  ];
}

function matchLabel(match: MatchOption | undefined, lookups: LookupState, timeFormat: TimeFormat): string {
  if (!match) return '';
  const time = formatTime(match.match_time, timeFormat);
  return `${formatDate(match.match_date)}${time ? ` ${time}` : ''} - ${lookupLabel(lookups, 'teams', match.team_id)} vs ${match.opponent_name || 'TBD'}`;
}

export function ParticipationEventsManager({ lookups, timeFormat, initialPlayerFilterId, readOnly = false }: ParticipationEventsManagerProps) {
  const auditUserId = useAuditUserId();
  const {
    state: savedViewState,
    setState: setSavedViewState,
    stateKey: viewStateKey,
    loadedStateKey,
  } = usePersistentViewState<ParticipationEventsViewState>({
    userId: auditUserId,
    routeKey: 'participation-events',
    contextKey: initialPlayerFilterId || 'all',
    defaultState: { search: '', teamFilters: [], playerFilter: initialPlayerFilterId || 'all', eventTypeFilter: 'all' },
  });
  const [events, setEvents] = useState<ParticipationEventRow[]>([]);
  const [matches, setMatches] = useState<MatchOption[]>([]);
  const [positions, setPositions] = useState<PositionOption[]>([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState(savedViewState.search);
  const [teamFilters, setTeamFilters] = useState<string[]>(savedViewState.teamFilters);
  const [playerFilter, setPlayerFilter] = useState(initialPlayerFilterId || savedViewState.playerFilter);
  const [eventTypeFilter, setEventTypeFilter] = useState(savedViewState.eventTypeFilter);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [viewing, setViewing] = useState<ParticipationEventRow | null>(null);
  const [form, setForm] = useState<EventForm>(emptyEventForm);
  const [snack, setSnack] = useState<Snack | null>(null);
  const restoredViewStateKey = useRef('');
  const [viewStateReady, setViewStateReady] = useState(false);

  const matchById = useMemo(() => new Map(matches.map((match) => [match.id, match])), [matches]);
  const positionById = useMemo(() => new Map(positions.map((position) => [position.id, position])), [positions]);
  const selectedMatch = matchById.get(form.match_event_id);
  const availablePositions = positions.filter((position) => !selectedMatch || position.league_configuration_id === selectedMatch.league_configuration_id);
  const accessibleTeamIds = useMemo(() => lookups.teams.map((team) => team.value), [lookups.teams]);

  usePersistentScrollRestoration({
    userId: auditUserId,
    routeKey: 'participation-events',
    contextKey: initialPlayerFilterId || 'all',
    ready: !loading,
  });

  useEffect(() => {
    if (!auditUserId || loadedStateKey !== viewStateKey) {
      setViewStateReady(false);
      return;
    }
    if (restoredViewStateKey.current === viewStateKey) {
      setViewStateReady(true);
      return;
    }
    setSearch(savedViewState.search);
    setTeamFilters(savedViewState.teamFilters);
    setPlayerFilter(initialPlayerFilterId || savedViewState.playerFilter);
    setEventTypeFilter(savedViewState.eventTypeFilter);
    restoredViewStateKey.current = viewStateKey;
    setViewStateReady(true);
  }, [auditUserId, initialPlayerFilterId, loadedStateKey, savedViewState, viewStateKey]);

  useEffect(() => {
    if (!viewStateReady || restoredViewStateKey.current !== viewStateKey) return;
    setSavedViewState({ search, teamFilters, playerFilter, eventTypeFilter });
  }, [eventTypeFilter, playerFilter, search, setSavedViewState, teamFilters, viewStateKey, viewStateReady]);

  const loadData = useCallback(async () => {
    if (accessibleTeamIds.length === 0) {
      setEvents([]);
      setMatches([]);
      setPositions([]);
      return;
    }
    setLoading(true);
    const [eventsResult, matchesResult, positionsResult] = await Promise.all([
      supabase
        .from('player_participation_events')
        .select('*')
        .is('deleted_at', null)
        .order('occurred_at', { ascending: false }),
      supabase
        .from('match_events')
        .select('id, team_id, league_configuration_id, match_date, match_time, opponent_name')
        .is('deleted_at', null)
        .in('team_id', accessibleTeamIds)
        .order('match_date', { ascending: false }),
      supabase
        .from('league_configuration_positions')
        .select('id, league_configuration_id, position_code, display_name')
        .is('deleted_at', null)
        .order('display_order', { ascending: true }),
    ]);
    setLoading(false);

    if (eventsResult.error || matchesResult.error || positionsResult.error) {
      setSnack({
        severity: 'error',
        message: eventsResult.error?.message || matchesResult.error?.message || positionsResult.error?.message || 'Unable to load participation events.',
      });
      return;
    }

    const loadedMatches = (matchesResult.data || []) as unknown as MatchOption[];
    const accessibleMatchIds = new Set(loadedMatches.map((match) => match.id));
    setEvents(((eventsResult.data || []) as unknown as ParticipationEventRow[]).filter((event) => accessibleMatchIds.has(event.match_event_id)));
    setMatches(loadedMatches);
    setPositions((positionsResult.data || []) as unknown as PositionOption[]);
  }, [accessibleTeamIds]);

  useEffect(() => {
    void loadData();
  }, [loadData]);

  useEffect(() => {
    setPlayerFilter(initialPlayerFilterId || 'all');
  }, [initialPlayerFilterId]);

  const filteredEvents = useMemo(() => {
    const needle = search.trim().toLowerCase();
    return events.filter((event) => {
      const match = matchById.get(event.match_event_id);
      const text = [
        formatDate(match?.match_date),
        lookupLabel(lookups, 'teams', match?.team_id),
        match?.opponent_name || '',
        lookupLabel(lookups, 'players', event.player_id),
        positionById.get(event.league_configuration_position_id || '')?.position_code || '',
        eventTypeLabel(event.participation_event_type),
        event.participation_event_reason || '',
        event.participation_event_notes || '',
      ].join(' ').toLowerCase();
      if (needle && !text.includes(needle)) return false;
      if (teamFilters.length > 0 && (!match?.team_id || !teamFilters.includes(match.team_id))) return false;
      if (playerFilter !== 'all' && event.player_id !== playerFilter) return false;
      if (eventTypeFilter !== 'all' && canonicalEventType(event.participation_event_type) !== eventTypeFilter) return false;
      return true;
    });
  }, [eventTypeFilter, events, lookups, matchById, playerFilter, positionById, search, teamFilters]);

  const openCreate = () => {
    if (readOnly) return;
    setViewing(null);
    setForm({
      ...emptyEventForm,
      match_event_id: matches[0]?.id || '',
      player_id: lookups.players[0]?.value || '',
    });
    setDialogOpen(true);
  };

  const openEdit = (event: ParticipationEventRow) => {
    if (readOnly) return;
    setViewing(null);
    setForm({
      id: event.id,
      match_event_id: event.match_event_id,
      player_id: event.player_id,
      league_configuration_position_id: event.league_configuration_position_id || '',
      participation_event_type: event.participation_event_type,
      participation_event_reason: isAllowedParticipationReason(event.participation_event_reason) ? event.participation_event_reason : 'Other',
      allocation_count_indicator: event.allocation_count_indicator,
      participation_event_notes: event.participation_event_notes || '',
    });
    setDialogOpen(true);
  };

  const saveEvent = async () => {
    if (readOnly) {
      setSnack({ severity: 'error', message: 'You have view-only access.' });
      return;
    }
    if (!form.match_event_id || !form.player_id || !form.participation_event_type) {
      setSnack({ severity: 'error', message: 'Match, player, and event type are required.' });
      return;
    }
    const payload = {
      match_event_id: form.match_event_id,
      player_id: form.player_id,
      league_configuration_position_id: form.league_configuration_position_id || null,
      participation_event_type: form.participation_event_type,
      participation_event_reason: form.participation_event_reason || 'Other',
      allocation_count_indicator: form.allocation_count_indicator,
      manual_allocation_override_indicator: form.allocation_count_indicator !== defaultAllocationForEventType(form.participation_event_type),
      participation_event_notes: form.participation_event_notes.trim() || null,
      updated_by: auditUserId || null,
      ...(form.id ? {} : { created_by: auditUserId || null }),
    };

    const { error } = form.id
      ? await supabase.from('player_participation_events').update(payload).eq('id', form.id)
      : await supabase.from('player_participation_events').insert(payload);

    if (error) {
      setSnack({ severity: 'error', message: error.message });
      return;
    }

    setDialogOpen(false);
    setSnack({ severity: 'success', message: form.id ? 'Participation event updated.' : 'Manual participation adjustment created.' });
    await loadData();
  };

  const softDeleteEvent = async (event: ParticipationEventRow) => {
    if (readOnly) return;
    if (!window.confirm(`Soft delete ${eventTypeLabel(event.participation_event_type)} event for ${lookupLabel(lookups, 'players', event.player_id)}?`)) return;
    const { error } = await supabase
      .from('player_participation_events')
      .update({
        deleted_at: new Date().toISOString(),
        deleted_by: auditUserId || null,
        delete_reason: 'Deleted from Participation Log management screen',
        updated_by: auditUserId || null,
      })
      .eq('id', event.id);

    if (error) {
      setSnack({ severity: 'error', message: error.message });
      return;
    }
    setSnack({ severity: 'success', message: 'Participation event soft deleted.' });
    await loadData();
  };

  const Detail = ({ label, value }: { label: string; value: string }) => (
    <Box>
      <Typography variant="caption" color="text.secondary">{label}</Typography>
      <Typography variant="body2">{value || '-'}</Typography>
    </Box>
  );

  return (
    <Stack spacing={2}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 2 }}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 700 }}>Participation Log</Typography>
          <Typography variant="body2" color="text.secondary">
            Search, edit, audit, and manually adjust player participation history.
          </Typography>
        </Box>
        {!readOnly && (
          <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate}>
            Create Manual Adjustment
          </Button>
        )}
      </Box>

      <Paper variant="outlined" sx={{ p: 2 }}>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '2fr 1fr 1fr 1fr' }, gap: 2 }}>
          <TextField label="Search" value={search} onChange={(event) => setSearch(event.target.value)} />
          <SeasonTeamMultiSelect lookups={lookups} value={teamFilters} onChange={setTeamFilters} />
          <FormControl fullWidth>
            <InputLabel>Player</InputLabel>
            <Select label="Player" value={playerFilter} onChange={(event) => setPlayerFilter(event.target.value)}>
              <MenuItem value="all">All Players</MenuItem>
              {lookups.players.map((player) => (
                <MenuItem key={player.value} value={player.value}>{player.label}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl fullWidth>
            <InputLabel>Event Type</InputLabel>
            <Select label="Event Type" value={eventTypeFilter} onChange={(event) => setEventTypeFilter(event.target.value)}>
              <MenuItem value="all">All Event Types</MenuItem>
              {participationEventTypeOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      </Paper>

      <Paper variant="outlined" sx={{ overflow: 'hidden' }}>
        {loading && <LinearProgress />}
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 700 }}>Date</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Team</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Opponent</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Player</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Line</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Event Type</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Reason</TableCell>
                <TableCell align="center" sx={{ fontWeight: 700 }}>Counts Toward Allocation</TableCell>
                <TableCell align="right" sx={{ fontWeight: 700 }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredEvents.map((event) => {
                const match = matchById.get(event.match_event_id);
                return (
                  <TableRow key={event.id} hover>
                    <TableCell>{formatDate(match?.match_date)}</TableCell>
                    <TableCell>{lookupLabel(lookups, 'teams', match?.team_id)}</TableCell>
                    <TableCell>{match?.opponent_name || 'TBD'}</TableCell>
                    <TableCell>{lookupLabel(lookups, 'players', event.player_id)}</TableCell>
                    <TableCell>{positionById.get(event.league_configuration_position_id || '')?.position_code || '-'}</TableCell>
                    <TableCell>{eventTypeLabel(event.participation_event_type)}</TableCell>
                    <TableCell>{event.participation_event_reason || '-'}</TableCell>
                    <TableCell align="center">{event.allocation_count_indicator ? 'Yes' : 'No'}</TableCell>
                    <TableCell align="right">
                      <Button size="small" startIcon={<VisibilityIcon />} onClick={() => setViewing(event)}>View</Button>
                      {!readOnly && (
                        <>
                          <Button size="small" startIcon={<EditIcon />} onClick={() => openEdit(event)}>Edit</Button>
                          <Button size="small" color="error" startIcon={<DeleteIcon />} onClick={() => void softDeleteEvent(event)}>Delete</Button>
                        </>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
              {filteredEvents.length === 0 && !loading && (
                <TableRow>
                  <TableCell colSpan={9}>
                    <Typography variant="body2" color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>
                      No participation events match the current filters.
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>{form.id ? 'Edit Participation Event' : 'Create Manual Adjustment'}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2, pt: 1 }}>
            <FormControl fullWidth required disabled={Boolean(form.id)}>
              <InputLabel>Match</InputLabel>
              <Select label="Match" value={form.match_event_id} onChange={(event) => setForm((current) => ({ ...current, match_event_id: event.target.value, league_configuration_position_id: '' }))}>
                {matches.map((match) => (
                  <MenuItem key={match.id} value={match.id}>{matchLabel(match, lookups, timeFormat)}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth required disabled={Boolean(form.id)}>
              <InputLabel>Player</InputLabel>
              <Select label="Player" value={form.player_id} onChange={(event) => setForm((current) => ({ ...current, player_id: event.target.value }))}>
                {lookups.players.map((player) => (
                  <MenuItem key={player.value} value={player.value}>{player.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth disabled={Boolean(form.id)}>
              <InputLabel>Line</InputLabel>
              <Select label="Line" value={form.league_configuration_position_id} onChange={(event) => setForm((current) => ({ ...current, league_configuration_position_id: event.target.value }))}>
                <MenuItem value="">No Line</MenuItem>
                {availablePositions.map((position) => (
                  <MenuItem key={position.id} value={position.id}>{position.position_code} - {position.display_name}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth required>
              <InputLabel>Event Type</InputLabel>
              <Select
                label="Event Type"
                value={form.participation_event_type}
                onChange={(event) => {
                  const eventType = event.target.value;
                  setForm((current) => ({
                    ...current,
                    participation_event_type: eventType,
                    allocation_count_indicator: defaultAllocationForEventType(eventType),
                  }));
                }}
              >
                {editableEventTypeOptions(form.participation_event_type).map((option) => (
                  <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth>
              <InputLabel>Reason</InputLabel>
              <Select label="Reason" value={form.participation_event_reason} onChange={(event) => setForm((current) => ({ ...current, participation_event_reason: event.target.value }))}>
                {participationEventReasonOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControlLabel
              control={<Checkbox checked={form.allocation_count_indicator} onChange={(event) => setForm((current) => ({ ...current, allocation_count_indicator: event.target.checked }))} />}
              label="Counts Toward Allocation"
            />
            <TextField
              label="Notes"
              value={form.participation_event_notes}
              onChange={(event) => setForm((current) => ({ ...current, participation_event_notes: event.target.value }))}
              multiline
              minRows={3}
              sx={{ gridColumn: '1 / -1' }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          {!readOnly && <Button variant="contained" onClick={() => void saveEvent()}>Save</Button>}
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(viewing)} onClose={() => setViewing(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Participation Event</DialogTitle>
        <DialogContent>
          {viewing && (
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, pt: 1 }}>
              <Detail label="Match" value={matchLabel(matchById.get(viewing.match_event_id), lookups, timeFormat)} />
              <Detail label="Player" value={lookupLabel(lookups, 'players', viewing.player_id)} />
              <Detail label="Line" value={positionById.get(viewing.league_configuration_position_id || '')?.position_code || ''} />
              <Detail label="Event Type" value={eventTypeLabel(viewing.participation_event_type)} />
              <Detail label="Reason" value={viewing.participation_event_reason || ''} />
              <Detail label="Counts Toward Allocation" value={viewing.allocation_count_indicator ? 'Yes' : 'No'} />
              <Detail label="Replaced Player" value={lookupLabel(lookups, 'players', viewing.replaced_player_id)} />
              <Detail label="Replacement Player" value={lookupLabel(lookups, 'players', viewing.replacement_player_id)} />
              <Detail label="Created By" value={lookupLabel(lookups, 'app_users', viewing.created_by)} />
              <Detail label="Updated By" value={lookupLabel(lookups, 'app_users', viewing.updated_by)} />
              <Box sx={{ gridColumn: '1 / -1' }}>
                <Detail label="Notes" value={viewing.participation_event_notes || ''} />
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setViewing(null)}>Close</Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={Boolean(snack)} autoHideDuration={5000} onClose={() => setSnack(null)}>
        {snack ? <Alert severity={snack.severity}>{snack.message}</Alert> : undefined}
      </Snackbar>
    </Stack>
  );
}
